import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Target, CalendarRange, UserCog, AlertCircle, User, Lightbulb, ClipboardCheck } from "lucide-react";
import { AttendanceRegisterEditor } from "@/components/AttendanceQuickPickerDialog";

function todayISO() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }

export default function TechToday() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coachMap, setCoachMap] = useState({});
  const [editRegisterId, setEditRegisterId] = useState(null);
  const [opening, setOpening] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, users] = await Promise.all([
        api.get("/technical/today", { params: { date: todayISO() } }),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setData(data);
      setCoachMap(Object.fromEntries((users.data || []).map((u) => [u.id, u.name])));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openRegister = useCallback(async (trainingId) => {
    if (!trainingId) return;
    setOpening(trainingId);
    try {
      const { data } = await api.post("/attendance", { training_id: trainingId, date: todayISO() });
      setEditRegisterId(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore apertura presenze");
    } finally { setOpening(null); }
  }, []);

  const siblings = useMemo(() => (data?.sessions || []).map((s) => ({
    training_id: s.training_id,
    register_id: null,
    title: (s.group_names || []).join(", ") || s.title || "Seduta",
    time: s.time || "",
    status: s.status === "completato" ? "completato" : "bozza",
  })), [data]);

  if (loading) return <div className="text-zinc-500 py-16 text-center">Caricamento…</div>;
  if (!data) return <div className="text-zinc-500 py-16 text-center">Nessun dato.</div>;

  const names = (ids) => (ids || []).map((i) => coachMap[i] || "—").join(" + ");

  return (
    <div className="space-y-6 max-w-3xl" data-testid="tech-today">
      {/* Da registrare — prima ciò che richiede azione */}
      {data.to_register.length > 0 && (
        <section className="bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="today-to-register">
          <div className="flex items-center gap-2 text-amber-800 font-semibold mb-2"><AlertCircle className="w-4 h-4" /> Ancora da registrare</div>
          <div className="space-y-1.5">
            {data.to_register.map((t) => (
              <button key={t.training_id} onClick={() => openRegister(t.training_id)} disabled={opening === t.training_id} data-testid={`to-register-${t.training_id}`}
                className="w-full flex items-center justify-between text-sm bg-white border border-amber-200 rounded px-3 py-2.5 hover:border-amber-400 text-left disabled:opacity-60">
                <span className="flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-amber-600" />{t.date} · {(t.group_names || []).join(", ") || t.title}</span>
                <span className="text-[11px] uppercase tracking-wider bg-amber-600 text-white rounded px-2 py-1">{opening === t.training_id ? "…" : "Presenze →"}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Sedute di oggi */}
      <section data-testid="today-sessions">
        <h2 className="font-heading text-lg font-semibold mb-3">Oggi · {data.date}</h2>
        {data.sessions.length === 0 ? (
          <div className="text-sm text-zinc-500 py-8 text-center border border-dashed border-zinc-200 rounded-md" data-testid="today-empty">Nessuna seduta che ti riguarda oggi.</div>
        ) : (
          <div className="space-y-3">
            {data.sessions.map((s) => (
              <div key={s.training_id} data-testid={`today-session-${s.training_id}`} className="bg-white border border-zinc-200 rounded-md p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-semibold">{(s.group_names || []).join(", ") || s.title || "Seduta"}</span>
                      <StatusPill status={s.status} />
                    </div>
                    <div className="text-xs text-zinc-500 font-mono mt-1 flex items-center gap-2 flex-wrap">
                      {s.time && <span><Clock className="w-3 h-3 inline mr-1" />{s.time}</span>}
                      <span>{s.duration || 90}′</span>
                      {s.present != null && s.status !== "pianificato" && <span>· {s.present} presenti</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => openRegister(s.training_id)}
                      disabled={opening === s.training_id}
                      data-testid={`today-attendance-${s.training_id}`}
                      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider bg-[#0052FF] text-white rounded px-3 py-2 hover:bg-blue-700 disabled:opacity-60"
                    >
                      <ClipboardCheck className="w-3.5 h-3.5" />{opening === s.training_id ? "…" : "Presenze"}
                    </button>
                    <Link to={`/allenamento/${s.training_id}`} data-testid={`today-open-${s.training_id}`}
                      className="text-xs uppercase tracking-wider border border-zinc-300 text-zinc-700 rounded px-3 py-2 hover:border-zinc-500">
                      {s.status === "completato" ? "Rivedi" : "Chiudi seduta"}
                    </Link>
                  </div>
                </div>
                {(s.microcycle_objective || s.session_objective) && (
                  <div className="mt-2 text-sm text-zinc-600 space-y-0.5">
                    {s.microcycle_objective && <div className="flex items-center gap-1.5"><CalendarRange className="w-3.5 h-3.5 text-zinc-400" /> {s.microcycle_objective}</div>}
                    {s.session_objective && <div className="flex items-center gap-1.5"><Target className="w-3.5 h-3.5 text-zinc-400" /> {s.session_objective}</div>}
                  </div>
                )}
                {(s.my_activities || []).length > 0 && (
                  <div className="mt-3 border-t border-zinc-100 pt-2">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Le tue attività</div>
                    <div className="space-y-1">
                      {s.my_activities.map((a, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span>{a.title} <span className="text-zinc-400">· {a.category || "—"}</span></span>
                          <span className="text-xs text-zinc-500 flex items-center gap-1"><UserCog className="w-3 h-3" />{names(a.coach_ids) || "—"} · {a.duration || 0}′</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Lezioni assegnate oggi */}
      {data.lessons.length > 0 && (
        <section data-testid="today-lessons">
          <h3 className="font-heading font-semibold mb-2 flex items-center gap-2"><User className="w-4 h-4 text-zinc-400" /> Lezioni di oggi</h3>
          <div className="space-y-1.5">
            {data.lessons.map((l) => (
              <div key={l.id} className="flex items-center justify-between text-sm bg-white border border-zinc-200 rounded px-3 py-2">
                <span>{l.athlete_name}{l.objective ? ` · ${l.objective}` : ""}</span>
                <span className="font-mono text-zinc-500">{l.duration || 0}′</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Suggerimenti atleti da seguire (info, non obbligo) */}
      {data.suggestions.length > 0 && (
        <section data-testid="today-suggestions">
          <h3 className="font-heading font-semibold mb-2 flex items-center gap-2"><Lightbulb className="w-4 h-4 text-amber-500" /> Atleti da seguire <span className="text-xs font-normal text-zinc-400">(suggerimento)</span></h3>
          <div className="flex flex-wrap gap-2">
            {data.suggestions.map((a) => (
              <div key={a.athlete_id} data-testid={`suggest-${a.athlete_id}`} className="text-sm bg-white border border-zinc-200 rounded-full px-3 py-1.5">
                {a.name} <span className={`ml-1 text-xs ${a.days_since == null ? "text-red-600" : a.days_since > 21 ? "text-red-600" : "text-zinc-400"}`}>{a.days_since == null ? "mai" : `${a.days_since}g`}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {editRegisterId && (
        <AttendanceRegisterEditor
          registerId={editRegisterId}
          siblings={siblings}
          onNavigate={(t) => openRegister(t.training_id)}
          onClose={() => { setEditRegisterId(null); load(); }}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = { completato: "bg-emerald-50 text-emerald-700", registro: "bg-amber-50 text-amber-700", pianificato: "bg-zinc-100 text-zinc-600" };
  const lbl = { completato: "chiusa", registro: "in registro", pianificato: "da fare" };
  return <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${map[status] || map.pianificato}`}>{lbl[status] || status}</span>;
}
